import { useState, useEffect, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api } from "../api/client";
import Bauchbinde from "../components/Bauchbinde";

let pdfjsReady = null;
function loadPdfJs() {
  if (window.pdfjsLib) return Promise.resolve(window.pdfjsLib);
  if (pdfjsReady) return pdfjsReady;
  pdfjsReady = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js";
    s.onload = () => {
      window.pdfjsLib.GlobalWorkerOptions.workerSrc =
        "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";
      resolve(window.pdfjsLib);
    };
    s.onerror = reject;
    document.head.appendChild(s);
  });
  return pdfjsReady;
}

export default function ProjektorAnsicht2() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [daten, setDaten] = useState(null);

  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const pdfDocRef = useRef(null);
  const lastAnlageIdRef = useRef(null);
  const renderTaskRef = useRef(null);

  const load = () => api.getProjektorDaten(id, 1, token).then(setDaten);

  useEffect(() => {
    load();
    const interval = setInterval(load, 3000);
    return () => clearInterval(interval);
  }, [id, token]);

  const anlage = daten?.aktive_anlage;

  useEffect(() => {
    if (!anlage) {
      pdfDocRef.current = null;
      lastAnlageIdRef.current = null;
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const pdfjs = await loadPdfJs();
        if (anlage.id !== lastAnlageIdRef.current) {
          lastAnlageIdRef.current = anlage.id;
          pdfDocRef.current = null;
          const token = localStorage.getItem("internal_token") || "";
          const res = await fetch(`/api/anlagen/${anlage.id}/datei.pdf`, {
            headers: { Authorization: "Bearer " + token },
          });
          if (!res.ok) { console.error("Anlage Status:", res.status); return; }
          const buf = await res.arrayBuffer();
          if (cancelled) return;
          pdfDocRef.current = await pdfjs.getDocument({ data: buf }).promise;
          api.setProjektorPdf(id, { pdf_seiten_gesamt: pdfDocRef.current.numPages }).catch(() => {});
        }
        if (cancelled || !pdfDocRef.current) return;
        const doc = pdfDocRef.current;
        const seite = Math.min(Math.max(1, daten.pdf_seite || 1), doc.numPages);
        const page = await doc.getPage(seite);
        const container = containerRef.current;
        if (!container) return;
        const baseViewport = page.getViewport({ scale: 1 });
        const fitScale = (container.clientWidth - 40) / baseViewport.width;
        const scale = fitScale * (daten.pdf_zoom || 1.0);
        const viewport = page.getViewport({ scale });
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d");
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        if (renderTaskRef.current) { try { renderTaskRef.current.cancel(); } catch {} }
        renderTaskRef.current = page.render({ canvasContext: ctx, viewport });
        try { await renderTaskRef.current.promise; }
        catch (e) { if (e?.name !== "RenderingCancelledException") console.error(e); }
        // Bildlauf-Position anwenden
        if (container && canvas) {
          const pct = (daten.pdf_scroll_pct || 0) / 100;
          const max = Math.max(0, canvas.height - container.clientHeight);
          container.scrollTop = max * pct;
        }
      } catch (e) {
        console.error("PDF render error:", e);
      }
    })();
    return () => { cancelled = true; };
  }, [anlage?.id, daten?.pdf_seite, daten?.pdf_zoom, daten?.pdf_scroll_pct, daten?.pdf_updated_at, id]);

  if (!daten) return (
    <div style={{ background: "#0d1b2a", minHeight: "100vh", display: "flex",
                  alignItems: "center", justifyContent: "center", color: "white", fontSize: 24 }}>Lädt…</div>
  );

  const top = daten.aktiver_top;

  return (
    <div style={{ background: "#0d1b2a", minHeight: "100vh", color: "white",
                  fontFamily: "Arial, sans-serif", padding: 40 }}>
      {anlage ? (
        <div style={{ height: "calc(100vh - 80px)", display: "flex", flexDirection: "column" }}>
          <div style={{ padding: "8px 16px", color: "#aaa", fontSize: 14,
                        display: "flex", justifyContent: "space-between" }}>
            <span>📄 {anlage.dateiname}</span>
            <span>Seite {daten.pdf_seite || 1} / {daten.pdf_seiten_gesamt || "?"} · {Math.round((daten.pdf_zoom || 1) * 100)}%</span>
          </div>
          <div ref={containerRef} style={{
            flex: 1, overflow: "auto", background: "#1a2a3a", padding: 20,
            display: "flex", justifyContent: "center", alignItems: "flex-start"
          }}>
            <canvas ref={canvasRef} style={{ background: "white", boxShadow: "0 8px 32px rgba(0,0,0,.5)" }} />
          </div>
        </div>
      ) : !top ? (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "80vh", flexDirection: "column", gap: 16 }}>
          <div style={{ fontSize: 60 }}>🚒</div>
          <div style={{ fontSize: 24, color: "#aaa" }}>{daten.sitzung.titel}</div>
          <div style={{ fontSize: 16, color: "#666" }}>Warte auf Tagesordnungspunkt…</div>
        </div>
      ) : (
        <div>
          <div style={{ borderBottom: "3px solid #cc0000", paddingBottom: 16, marginBottom: 32 }}>
            <div style={{ fontSize: 16, color: "#aaa", marginBottom: 4 }}>TOP {top.position}</div>
            <div style={{ fontSize: 36, fontWeight: 700 }}>{top.titel}</div>
          </div>
          {top.beschreibung && (
            <div style={{ fontSize: 20, lineHeight: 1.6, whiteSpace: "pre-wrap", marginBottom: 32 }}>
              {top.beschreibung}
            </div>
          )}
          {top.abstimmung_ergebnis && (
            <div style={{ background: "rgba(255,255,255,0.05)", padding: 24, borderRadius: 8, marginTop: 32 }}>
              <div style={{ fontSize: 14, color: "#aaa", marginBottom: 8 }}>Abstimmungsergebnis</div>
              <div style={{ fontSize: 28, fontWeight: 700 }}>{top.abstimmung_ergebnis}</div>
              <div style={{ fontSize: 16, color: "#aaa", marginTop: 8 }}>
                Ja: {top.abstimmung_ja || 0} · Nein: {top.abstimmung_nein || 0} · Enthaltungen: {top.abstimmung_enthaltung || 0}
              </div>
            </div>
          )}
        </div>
      )}
      <Bauchbinde text={daten.bauchbinde_text} sichtbar={daten.bauchbinde_sichtbar} />
    </div>
  );
}
